import React, {PropsWithChildren} from 'react';
import Button from "react-bootstrap/Button";
import {getCurrencySymbol} from "./AmountSpan";

/**
 * A selectable option button used inside billing plan UIs.
 *
 * Renders a Bootstrap button that visually highlights its border when
 * `selected` is `true` (darker, thicker border).  Pass any content as
 * `children` to render inside the button.
 *
 * @param selected - Whether this option is currently chosen.
 * @param onClick  - Click handler (optional).
 * @param children - Content to display inside the button.
 */
export default function OptionButton({ children, selected, onClick = undefined }: PropsWithChildren<{
    selected: boolean; onClick?: () => void;
}>) {
    return (
        <Button
            className={`flex-grow-1 text-dark d-flex flex-column py-2 px-4 rounded-4 align-items-center border border-1 ${selected ? 'border-neutral-700 border-2': 'border-1 border-neutral-400'} mt-2`}
            variant=""
            onClick={onClick}
        >
            {children}
        </Button>
    );
}

/**
 * Displays the saving when choosing an annual plan over equivalent monthly billing.
 *
 * - Savings **below 20** (in the major currency unit) are shown as a
 *   percentage: `"Save 15%!"`.
 * - Savings **of 20 or more** are shown as an absolute amount:
 *   `"€ 24.00 savings!"`.
 *
 * @param mainPriceAmount  - Annual price in the smallest currency unit (cents).
 * @param downSellAmount   - Equivalent monthly price in cents.
 * @param currency         - ISO 4217 code (`"eur"` or `"usd"`).
 */
function Discount({ mainPriceAmount, downSellAmount, currency }: {
    currency: 'eur' | 'usd';
    mainPriceAmount: number;
    downSellAmount: number;
}) {
    const annualDownSellAmount = 12 * downSellAmount;
    const savingInAbsolute = ((annualDownSellAmount - mainPriceAmount) / 100);
    const savingInPercentage = Math.round(savingInAbsolute * 100 / annualDownSellAmount * 100);

    return (
        <div className="text-success text-sm">
            {savingInAbsolute < 20
                ? `Save ${savingInPercentage}%!`
                : <>{getCurrencySymbol(currency)}{savingInAbsolute.toFixed(2)} savings!</>}
        </div>
    )
}

OptionButton.Discount = Discount;
